/* PLP-only module: facets/sort/load-more via Section Rendering API */

const escP = MV.escapeHtml;

class MVFacets extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;
    this.sectionId = this.getAttribute("section-id");
    this.sheet = this.querySelector(".mv-plp__sheet");

    this.addEventListener("change", (e) => {
      if (e.target.matches("[data-sort]")) this.applyParam("sort_by", e.target.value);
      else if (e.target.closest("form[data-facets]")) this.applyForm(e.target.closest("form[data-facets]"));
    });
    this.addEventListener("click", (e) => {
      const chip = e.target.closest(".mv-plp__chip");
      if (chip) { e.preventDefault(); this.navigate(chip.href); return; }
      const more = e.target.closest("[data-load-more]");
      if (more) { e.preventDefault(); this.loadMore(more.href); return; }
      if (e.target.closest("[data-open-filters]")) this.openSheet();
      if (e.target === this.sheet || e.target.closest("[data-sheet-close]")) this.closeSheet();
    });
    this.addEventListener("submit", (e) => {
      const sortForm = e.target.closest("[data-sort-form]");
      if (sortForm) { e.preventDefault(); this.applyParam("sort_by", sortForm.querySelector("[data-sort]").value); return; }
      const form = e.target.closest("form[data-facets]");
      if (form) { e.preventDefault(); this.applyForm(form); }
    });
    this.sheet?.addEventListener("cancel", (e) => { e.preventDefault(); this.closeSheet(); });
    this.onPop = () => this.navigate(location.href, { push: false });
    window.addEventListener("popstate", this.onPop);
  }
  disconnectedCallback() { window.removeEventListener("popstate", this.onPop); }

  // lock/unlock only when the sheet actually toggles — navigate() calls closeSheet()
  // unconditionally on every request, so an unguarded unlock would desync the ref-counter
  openSheet() { if (this.sheet && !this.sheet.open) { this.sheet.showModal(); MV.lockScroll(); } }
  closeSheet() { if (this.sheet?.open) { this.sheet.close(); MV.unlockScroll(); } }

  applyParam(key, value) {
    const url = new URL(location.href);
    url.searchParams.set(key, value);
    url.searchParams.delete("page");
    this.navigate(url.href);
  }
  applyForm(form) {
    const url = new URL(location.pathname, location.origin);
    const q = new URL(location.href).searchParams.get("q");
    if (q) url.searchParams.set("q", q);
    const type = new URL(location.href).searchParams.get("type");
    if (type) url.searchParams.set("type", type);
    const sort = this.querySelector("[data-sort]")?.value;
    if (sort) url.searchParams.set("sort_by", sort);
    new FormData(form).forEach((v, k) => { if (k === "q" || k === "type") return; if (v !== "") url.searchParams.append(k, v); });
    this.navigate(url.href);
  }
  async navigate(href, { push = true } = {}) {
    this.closeSheet();
    this.navController?.abort();
    this.navController = new AbortController();
    const grid = this.querySelector("#ProductGrid");
    grid?.classList.add("is-loading");
    try {
      const url = new URL(href, location.origin);
      url.searchParams.set("section_id", this.sectionId);
      const res = await fetch(url, { signal: this.navController.signal });
      if (!res.ok) throw new Error(res.status);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      ["#ProductGrid", "#ProductCount", "#FacetChips", "#LoadMoreWrap"].forEach((sel) => {
        const from = doc.querySelector(sel);
        const to = this.querySelector(sel);
        if (from && to) to.replaceWith(from);
      });
      // refresh both filter forms to match new state
      ["#FacetsDesktop", "#FacetsMobile"].forEach((sel) => {
        const from = doc.querySelector(sel);
        const to = this.querySelector(sel);
        if (from && to) to.replaceWith(from);
      });
      const newSort = doc.querySelector("[data-sort]");
      const curSort = this.querySelector("[data-sort]");
      if (newSort && curSort) curSort.value = newSort.value;
      if (push) {
        const clean = new URL(href, location.origin);
        clean.searchParams.delete("section_id");
        history.pushState({}, "", clean);
      }
      this.querySelector("#ProductCount")?.scrollIntoView({ block: "nearest" });
    } catch (err) {
      if (err?.name === "AbortError") return; // superseded
      window.MV?.toast("Couldn't update — please retry");
    } finally {
      // re-query: grid node may have been replaced on success
      this.querySelector("#ProductGrid")?.classList.remove("is-loading");
    }
  }
  async loadMore(href) {
    this.navController?.abort();
    this.navController = new AbortController();
    const btn = this.querySelector("[data-load-more]");
    if (btn) { btn.textContent = "Loading…"; btn.setAttribute("aria-disabled", "true"); }
    try {
      const url = new URL(href, location.origin);
      url.searchParams.set("section_id", this.sectionId);
      const res = await fetch(url, { signal: this.navController.signal });
      if (!res.ok) throw new Error(res.status);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const newItems = doc.querySelectorAll("#ProductGrid > li");
      const grid = this.querySelector("#ProductGrid");
      newItems.forEach((li) => grid.appendChild(li));
      const newWrap = doc.querySelector("#LoadMoreWrap");
      if (newWrap) this.querySelector("#LoadMoreWrap").replaceWith(newWrap);
      // a11y: move focus to the first newly-added card so keyboard/AT users land on the new content
      newItems[0]?.querySelector(".mv-card__title a")?.focus();
    } catch (err) {
      if (err?.name === "AbortError") return;
      if (btn) { btn.textContent = "Load more"; btn.removeAttribute("aria-disabled"); }
      window.MV?.toast("Couldn't load more — please retry");
    }
  }
}
customElements.define("mv-facets", MVFacets);

class MVWishlistPage extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;
    this.grid = this.querySelector("#WishlistGrid");
    this.count = this.querySelector("#WishlistCount");
    this.empty = this.querySelector("#WishlistEmpty");
    this._renderToken = 0;
    this.offWl = MV.on("wishlist:updated", () => this.render(true));
    this.render(false);
  }
  disconnectedCallback() { this.offWl?.(); }
  async render(soft) {
    const handles = MV.wishlist.all();
    const token = ++this._renderToken;
    if (!handles.length) {
      this.count.textContent = "";
      this.empty.hidden = false;
      this.grid.innerHTML = "";
      return;
    }
    if (!soft) this.grid.innerHTML = '<li><div class="mv-skeleton" style="aspect-ratio:3/4"></div></li>'.repeat(Math.min(handles.length, 8));
    const root = MV.root();
    const results = await Promise.all(handles.map(async (h) => {
      try { return { h, p: await (await fetch(`${root}products/${encodeURIComponent(h)}.js`)).json() }; }
      catch { return { h, p: null }; }
    }));
    const dead = results.filter((r) => !r.p).map((r) => r.h);
    if (dead.length) MV.wishlist.prune(dead); // may synchronously trigger a newer render via wishlist:updated
    if (token !== this._renderToken) return; // a newer render has since taken over — don't clobber it
    const items = results.map((r) => r.p).filter(Boolean);
    this.count.textContent = items.length ? `${items.length} saved item${items.length > 1 ? "s" : ""}` : "";
    this.empty.hidden = items.length > 0;
    this.grid.innerHTML = items.map((p) => `
      <li>
        <div class="mv-card${p.available ? "" : " is-soldout"}">
          <div class="mv-card__visual">
            <a href="${escP(p.url)}" class="mv-card__media" tabindex="-1" aria-hidden="true">
              ${p.featured_image ? `<img class="mv-card__img" src="${escP(p.featured_image)}${p.featured_image.includes("?") ? "&" : "?"}width=400" alt="" loading="lazy">` : ""}
              ${p.available ? "" : '<span class="mv-card__soldout">Sold out</span>'}
            </a>
            <mv-wishlist-button class="mv-card__wish" data-handle="${escP(p.handle)}">
              <button type="button" aria-label="Remove from wishlist" aria-pressed="true"></button>
            </mv-wishlist-button>
            ${p.available ? `<button type="button" class="mv-card__quickadd"
                onclick="window.MV && MV.emit('quickadd:open', { handle: '${escP(p.handle)}', source: this })">+ Quick add</button>` : ""}
          </div>
          <div class="mv-card__info">
            <h3 class="mv-card__title"><a href="${escP(p.url)}">${escP(p.title)}</a></h3>
            <div class="mv-card__price"><span class="mv-card__price-now">${MV.money(p.price)}</span></div>
          </div>
        </div>
      </li>`).join("");
    // inject heart icons into wishlist buttons
    this.grid.querySelectorAll(".mv-card__wish button").forEach((b) => {
      b.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 21C7 16.5 3 13.2 3 9a5 5 0 0 1 9-3 5 5 0 0 1 9 3c0 4.2-4 7.5-9 12z"/></svg>';
      b.classList.add("is-active");
    });
  }
}
customElements.define("mv-wishlist-page", MVWishlistPage);
